/**
 * Workflow App Component
 * Main entry point that orchestrates Dashboard → Workflow loading → Editor rendering
 * Now with integrated AppLayout (Header + Left Menu Bar) and Module viewer.
 */

import React, { useState, useEffect } from 'react';
import { HilProvider } from '@bizfirst/hil-react';
import { HilFormRendererProvider } from '@bizfirst/hil-ui';
import '@bizfirst/hil-ui/styles';
import '@bizfirst/hil-ui-chat-window/styles';
import { AtlasFormPresentationContent } from '@bizfirst/hil-ui-atlas-forms';
import { inboxItemHilSubmitClient, apiConfig } from '@flow-studio/api';
import { HilPendingLoader } from './HIL/HilPendingLoader';
import { AppLayout, MenuView, type BreadcrumbItem } from './Layout/AppLayout';
import { SettingsIcon, FolderIcon, BizFirstIcon } from '@bizfirst/common-react';
import { FlowStudioBuildWithAIPage } from './Shared/FlowStudioBuildWithAIPage';
import { UnifiedDashboard, DashboardView } from './Dashboard/UnifiedDashboard';
import { WorkflowEditor } from './WorkflowEditor';
import { Settings, type Tab as SettingsTab } from './Settings/Settings';
import { ObserverConfigPage } from '../../../../../../packages/observer-panel/src/components/ObserverConfigPage';
import { HilV13TestPage } from './HIL/HilV13TestPage';
import { ModuleViewer } from './Modules/ModuleViewer';
import { ToastContainer } from './Support/ToastContainer';
import { FlowObserverInit } from './FlowObserverInit';
import { type ModuleItem } from '../modules/moduleRegistry';
import { Project, Process, ProcessThread } from '@flow-studio/api';
import {
  processThreadLoadingService,
  CompleteProcessThreadData,
} from '@flow-studio/services';
import { useWorkflowStore, initializeNodeTemplates } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import {
  publishProcessRendered,
  publishProcessThreadRendered,
  publishProcessThreadDataLoaded,
  getErrorMessage,
  isDevelopment,
} from 'flow-studio-core';

// Shared agent repo for every studio, same URL as App Studio's APP_STUDIO_AI_AGENT_GITHUB_URL
// (app-studio-designer's AppShell.tsx) -- fetches Claudia/AGENTS.md directly, which routes
// "Flow Studio" requests to Claudia/Agents/Builders/WorkflowDeveloper.
// NOTE: raw.githubusercontent.com is not reliably reachable by the Claude in Chrome agent
// (fails as a false 404, likely an extension site-permission gap) -- use the github.com blob
// URL, which the agent can open and read directly.
const FLOW_STUDIO_AI_AGENT_GITHUB_URL =
  'https://github.com/BizFirstAi/SoftwareEngineerAiAgents/blob/main/Claudia/AGENTS.md';

/**
 * App State
 */
type AppState =
  | { view: 'dashboard'; dashboardView?: DashboardView }
  | { view: 'loading'; project: Project }
  | { view: 'designer'; processThreadData: CompleteProcessThreadData }
  | { view: 'settings'; defaultTab?: SettingsTab }
  | { view: 'observer' }
  | { view: 'hil-test' }
  | { view: 'build-with-ai' }
  | { view: 'module'; moduleItem: ModuleItem; fromTab: SettingsTab }
  | { view: 'open-target-error'; message: string };

// Maps a full-page-navigation landing path to the AppState view it should open on mount —
// same set of non-designer views AppLayout's own ROUTE_TO_VIEW (Layout/AppLayout.tsx) knows
// how to highlight in the sidebar. Needed so a plain <a href="/settings"> style link (e.g.
// AuthHeaderControls' "View App Settings" row — see AppLayout.tsx) actually lands on Settings
// instead of silently resetting to the dashboard on the resulting full reload.
// '/apps' (Dev Tools) is also excluded — it is no longer an internal view at all; clicking it
// in the sidebar now navigates the browser straight to the developer-portal app (see
// AppLayout.tsx's handleNavigate), so this app never renders anything for that path.
// '/designer' now shows the dashboard view - user must select a project to open designer
const ROUTE_TO_INITIAL_VIEW: Partial<Record<string, AppState['view']>> = {
  '/dashboard': 'dashboard',
  '/designer': 'dashboard', // Designer nav shows dashboard for project selection
  '/settings': 'settings',
  '/observer': 'observer',
  '/hil-test': 'hil-test',
  '/build-with-ai': 'build-with-ai',
};

// This app can be deployed under a sub-path (e.g. production serves it at
// http://host/flowstudio/, configured via vite.config.ts's VITE_BASE_URL), in which case
// `window.location.pathname` is "/flowstudio/settings", not "/settings" — a bare lookup in
// ROUTE_TO_INITIAL_VIEW above would never match, silently defaulting every hard-refresh/
// deep-link of a non-dashboard route back to the dashboard (real prod bug: refreshing
// /flowstudio/settings or /flowstudio/observer would sometimes land back on the project list).
// import.meta.env.BASE_URL is Vite's own normalized form of that same `base` config value
// ("/flowstudio/" or "/"), so stripping it here keeps this lookup — and the URL-sync effect
// below, which must prepend it back — correct under both a root and a sub-path deployment.
const BASE_PATH = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');

function stripBasePath(pathname: string): string {
  if (BASE_PATH && pathname.startsWith(BASE_PATH)) {
    const rest = pathname.slice(BASE_PATH.length);
    return rest.startsWith('/') ? rest : `/${rest}`;
  }
  return pathname;
}

// Mirror image of ROUTE_TO_INITIAL_VIEW above: given the *current* AppState, what should the
// address bar show? Drives the URL-sync effect in WorkflowApp so opening a workflow (via the
// dashboard's "Design" button, a breadcrumb, or a query-param deep link) is reflected in the
// URL — not just held in memory — and so the URL can be copied/bookmarked/reloaded to land
// back on that exact workflow (Binoy's "show the workflow on the URL" request).
// 'loading' and 'open-target-error' are deliberately excluded (return null, meaning "leave the
// URL alone") — they're transient: touching the URL for 'loading' would flash a wrong URL
// mid-navigation, and touching it for 'open-target-error' would overwrite the just-pasted
// deep-link URL before the user has had a chance to see/copy/retry it.
function getUrlForAppState(state: AppState): string | null {
  switch (state.view) {
    case 'designer':
      return `/designer?workflowId=${state.processThreadData.processThread.ProcessThreadID}`;
    case 'dashboard':
      return '/dashboard';
    case 'settings':
    case 'module':
      return '/settings';
    case 'observer':
      return '/observer';
    case 'hil-test':
      return '/hil-test';
    case 'build-with-ai':
      return '/build-with-ai';
    default:
      return null;
  }
}

/**
 * Workflow App Component
 */
export const WorkflowApp: React.FC = () => {
  const [appState, setAppState] = useState<AppState>(() => {
    const view = ROUTE_TO_INITIAL_VIEW[stripBasePath(window.location.pathname)];
    return view ? ({ view } as AppState) : { view: 'dashboard' };
  });
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const { theme, showToast } = useUIStore();

  /**
   * Initialize Node Templates on mount
   */
  useEffect(() => {
    let isMounted = true;

    const initTemplates = async () => {
      try {
        console.log('[WorkflowApp] Starting node template initialization...');
        await initializeNodeTemplates();
        if (isMounted) setTemplatesLoaded(true);
      } catch (error) {
        if (isMounted) {
          console.error('Failed to initialize node templates:', error);
          setTemplatesLoaded(true);
        }
      }
    };

    initTemplates();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Show hint when navigating to designer without a project selected
   */
  useEffect(() => {
    if (window.location.pathname === '/designer' && appState.view === 'dashboard') {
      console.log('[WorkflowApp] Designer view selected - showing project list for selection');
      // Optionally show a toast to guide the user
      // showToast('info', 'Select a project to open in the designer');
    }
  }, []);

  // Monotonically increasing navigation-request counter. Every async "resolve an ID (or set
  // of IDs) to a workflow and open it" operation — the query-param deep link below, browser
  // back/forward, and handleProcessThreadSelect further down — captures the counter's value
  // before it starts fetching, then only commits its result to appState if the counter still
  // matches when the fetch resolves. If a newer navigation started in between (e.g. the user
  // clicked a different "Design" button, or fired the browser Back button, before the first
  // request's network round-trip finished), the counter has moved on and the stale result is
  // discarded instead of clobbering the newer, already-in-flight-or-settled navigation.
  // Real bug this closes: without this guard, "which workflow ends up on screen" was decided
  // by which async chain happened to resolve LAST, not which one the user clicked/navigated to
  // last — a slow first request finishing after a fast second one would silently overwrite it.
  const navSeqRef = React.useRef(0);

  // Set by the popstate handler right before it calls setAppState, so the URL-sync effect
  // below (which reacts to every appState change) knows this particular change came FROM the
  // browser's own back/forward navigation — the URL is already correct in that case, so the
  // sync effect must not push a brand new history entry on top of it.
  const suppressUrlSyncRef = React.useRef(false);

  // Guards the URL-sync effect against a real ordering bug: appState starts as 'dashboard'
  // on every mount (see useState initializer above) even when the URL that was actually
  // loaded/pasted is a designer deep link — appState only flips to 'designer' once
  // openWorkflowFromParams' fetch resolves, which can't happen until templatesLoaded is true.
  // Without this guard, the URL-sync effect would see appState:'dashboard' on that first
  // render and immediately pushState('/dashboard'), overwriting the pasted deep-link URL's
  // query string before the mount effect below ever gets a chance to read it. Stays false
  // until the initial-mount deep-link check has actually run (either "no target in the URL"
  // or "target found and openWorkflowFromParams has been kicked off").
  const initialUrlCheckedRef = React.useRef(false);

  /**
   * Resolve the most-specific of threadId/workflowId, processId, projectId present in the
   * given URL search params and open that workflow in the Designer (or a persistent error
   * view if it can't be resolved). Also accepts a token param so a cross-origin caller (e.g.
   * HIL WorkDesk) can seed flow-studio's auth.
   *
   * Single canonical resolver, reused by both the initial-mount deep link (a fresh page load
   * or a pasted URL) and the popstate handler (browser back/forward) below — a URL resolves
   * to the same workflow the same way regardless of how the app arrived at it, rather than
   * each caller re-implementing its own fetch chain.
   *
   * Single canonical loader: ProcessThreadLoadingService (@flow-studio/services) already
   * implements all three levels of default-propagation — loadByThread (exact), loadByProcess
   * (first thread of that process), loadByProject (first process, then first thread). Per
   * Binoy's explicit direction: "if one value is missing assume other values and show the
   * designer" — most-specific-wins, missing levels default to "first".
   *
   * On failure, this sets a persistent 'open-target-error' view rather than only a toast —
   * a toast alone left the user silently stranded on the dashboard/list with no visible
   * explanation of why the deep link didn't open, which read as "it just shows the list".
   */
  const openWorkflowFromParams = React.useCallback(async (params: URLSearchParams) => {
    // Seed auth token from caller if provided (HIL app is on a different port)
    const tokenParam = params.get('token');
    if (tokenParam) {
      localStorage.setItem('authToken', tokenParam);
    }

    const threadIdParam = params.get('threadId') || params.get('workflowId');
    const processIdParam = params.get('processId');
    const projectIdParam = params.get('projectId');
    if (!threadIdParam && !processIdParam && !projectIdParam) {
      setAppState({ view: 'dashboard' });
      return;
    }

    const parsePositiveInt = (raw: string | null): number | null => {
      if (!raw) return null;
      const n = parseInt(raw, 10);
      return isNaN(n) || n <= 0 ? null : n;
    };

    const threadId = parsePositiveInt(threadIdParam);
    const processId = parsePositiveInt(processIdParam);
    const projectId = parsePositiveInt(projectIdParam);

    const seq = ++navSeqRef.current;

    try {
      let processThreadData: CompleteProcessThreadData | null;
      let label: string;

      if (threadId) {
        processThreadData = await processThreadLoadingService.loadByThread(threadId);
        label = `workflow ${threadId}`;
      } else if (processId) {
        processThreadData = await processThreadLoadingService.loadByProcess(processId);
        label = `process ${processId}`;
      } else {
        processThreadData = await processThreadLoadingService.loadByProject(projectId!);
        label = `project ${projectId}`;
      }

      if (navSeqRef.current !== seq) return; // a newer navigation started — discard this result

      if (processThreadData) {
        setAppState({ view: 'designer', processThreadData });
      } else {
        console.error(`[WorkflowApp] Could not resolve ${label} to an openable workflow.`);
        setAppState({
          view: 'open-target-error',
          message: `Couldn't open ${label} — it may not exist, have no processes/workflows yet, or you may not have access. Check the console for details.`,
        });
      }
    } catch (err) {
      if (navSeqRef.current !== seq) return;
      console.error('[WorkflowApp] auto-open failed:', err);
      setAppState({
        view: 'open-target-error',
        message: 'Failed to open the requested workflow — check the console for details.',
      });
    }
  }, []);

  /**
   * Auto-open the designer when projectId/processId/threadId (or workflowId, an alias for
   * threadId) is passed as a URL query param on initial mount — a fresh page load, or a
   * deep-link URL (e.g. from HIL WorkDesk, or one this app itself wrote via the URL-sync
   * effect below and the user later pasted into a new tab / reloaded).
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const hasTarget =
      params.get('threadId') || params.get('workflowId') || params.get('processId') || params.get('projectId');
    if (!hasTarget) {
      initialUrlCheckedRef.current = true;
      return;
    }

    // Wait for node templates before opening the canvas — leave initialUrlCheckedRef false
    // in the meantime so the URL-sync effect doesn't overwrite this URL first (see its comment).
    if (!templatesLoaded) return;

    initialUrlCheckedRef.current = true;
    openWorkflowFromParams(params);
  }, [templatesLoaded, openWorkflowFromParams]);

  /**
   * Browser back/forward. The URL-sync effect below pushes a new history entry every time the
   * Designer opens a (new) workflow or the user returns to the Dashboard/Settings/etc., so
   * popstate always lands on a URL this same resolver (openWorkflowFromParams) knows how to
   * interpret — re-resolving from the URL rather than trusting any cached in-memory state,
   * since a fresh popstate can point at a workflow this tab never actually loaded (e.g. two
   * "Design" clicks in the same session, then Back past both).
   */
  useEffect(() => {
    const handlePopState = () => {
      suppressUrlSyncRef.current = true;
      const params = new URLSearchParams(window.location.search);
      const hasTarget =
        params.get('threadId') || params.get('workflowId') || params.get('processId') || params.get('projectId');
      if (hasTarget) {
        openWorkflowFromParams(params);
      } else {
        setAppState({ view: 'dashboard' });
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [openWorkflowFromParams]);

  /**
   * Keep the browser URL in sync with appState — this is what actually makes "open a workflow
   * in the Designer" show up on the URL (Binoy's request): every time appState settles on a
   * new Designer/Dashboard/Settings/etc. view, push the matching URL (getUrlForAppState above)
   * so it can be copied, bookmarked, or reloaded, and so back/forward has a real history entry
   * to land on. Skipped when the appState change itself came FROM popstate (suppressUrlSyncRef)
   * — the URL is already correct in that case — and for transient views (getUrlForAppState
   * returns null for 'loading'/'open-target-error'), so the address bar doesn't flash or get
   * overwritten mid-navigation.
   */
  useEffect(() => {
    if (!initialUrlCheckedRef.current) return; // initial deep-link check hasn't run yet — don't stomp its URL
    if (suppressUrlSyncRef.current) {
      suppressUrlSyncRef.current = false;
      return;
    }
    const relativeUrl = getUrlForAppState(appState);
    if (!relativeUrl) return;
    const targetUrl = BASE_PATH + relativeUrl;
    const currentUrl = window.location.pathname + window.location.search;
    if (targetUrl !== currentUrl) {
      window.history.pushState(null, '', targetUrl);
    }
  }, [appState]);

  /**
   * Handle process thread selection from unified dashboard
   */
  const handleProcessThreadSelect = async (
    project: Project,
    process: Process,
    processThread: ProcessThread
  ) => {
    setAppState({ view: 'loading', project });
    // Same stale-response guard as openWorkflowFromParams above — if the user clicks a
    // different "Design" button (or a deep link resolves) before this fetch finishes, its
    // result must not overwrite whatever that newer navigation already settled on.
    const seq = ++navSeqRef.current;

    try {
      const processThreadData = await processThreadLoadingService.loadByThread(
        processThread.ProcessThreadID,
        project,
        process,
        processThread
      );

      if (navSeqRef.current !== seq) return; // a newer navigation started — discard this result

      if (!processThreadData) {
        showToast('error', 'Failed to load process thread data');
        setAppState({ view: 'dashboard' });
        return;
      }

      await publishProcessRendered({
        project: processThreadData.project,
        process: processThreadData.process,
        processThreadCount: 1,
        tenantId: project.TenantID,
      });

      await publishProcessThreadDataLoaded({
        project: processThreadData.project,
        process: processThreadData.process,
        processThread: processThreadData.processThread,
        processElements: processThreadData.processElements,
        connections: processThreadData.connections,
        tenantId: project.TenantID,
      });

      await publishProcessThreadRendered({
        project: processThreadData.project,
        process: processThreadData.process,
        processThread: processThreadData.processThread,
        processElements: processThreadData.processElements,
        connections: processThreadData.connections,
        tenantId: project.TenantID,
      });

      setAppState({ view: 'designer', processThreadData });
    } catch (error) {
      if (navSeqRef.current !== seq) return; // a newer navigation started — discard this result
      console.error('Error loading workflow:', error);
      const errorMessage = getErrorMessage(error, 'An error occurred while loading the workflow');
      const displayMessage = isDevelopment()
        ? `Error loading workflow:\n\n${errorMessage}`
        : 'An error occurred while loading the workflow';
      showToast('error', displayMessage);
      setAppState({ view: 'dashboard' });
    }
  };

  /**
   * Handle navigation from left menu bar
   */
  const handleMenuViewChange = (menuView: MenuView) => {
    if (menuView === 'dashboard') {
      setAppState({ view: 'dashboard' });
    } else if (menuView === 'settings') {
      setAppState({ view: 'settings' });
    } else if (menuView === 'observer') {
      setAppState({ view: 'observer' });
    } else if (menuView === 'hil-test') {
      setAppState({ view: 'hil-test' });
    }
    // 'designer' is only accessible through project selection
  };

  /**
   * Get current menu view based on app state.
   * Both 'settings' and 'module' views highlight the Settings nav item. 'build-with-ai' has no
   * sidebar item of its own any more (2026-09-13: moved to a header button, see AppLayout.tsx's
   * `onBuildWithAI` prop) — falls back to 'dashboard' rather than 'settings' so nothing in the
   * sidebar is misleadingly highlighted while it's open.
   */
  const getCurrentMenuView = (): MenuView => {
    if (appState.view === 'dashboard' || appState.view === 'loading' || appState.view === 'open-target-error' || appState.view === 'build-with-ai') return 'dashboard';
    if (appState.view === 'designer') return 'designer';
    if (appState.view === 'observer') return 'observer';
    if (appState.view === 'hil-test') return 'hil-test';
    return 'settings';
  };

  /**
   * Build header breadcrumbs when in designer view
   */
  const getHeaderBreadcrumbs = React.useCallback((): BreadcrumbItem[] | undefined => {
    if (appState.view === 'designer') {
      const { project, process, processThread } = appState.processThreadData;
      // Defensive guard (real, reported crash: "Cannot read properties of undefined (reading
      // 'Name')") — `ProcessThreadLoadingService` no longer returns a `CompleteProcessThreadData`
      // with a missing `project`/`process` (fixed at the source), but this component must not
      // trust that promise blindly either — a stale/cached `appState` from a future code path,
      // or a project/process deleted out from under an already-open Designer session, could hit
      // the same shape. Degrade to a placeholder label instead of crashing the whole app.
      return [
        {
          label: project?.Name ?? 'Untitled Project',
          onClick: () => setAppState({ view: 'dashboard', dashboardView: { type: 'projects' } }),
        },
        {
          label: process?.Name ?? 'Untitled Process',
          onClick: () => setAppState({ view: 'dashboard', dashboardView: { type: 'processes', project } }),
        },
        { label: processThread?.Name ?? 'Untitled Workflow' },
      ];
    }
    return undefined;
  }, [appState]);

  /**
   * Handle save workflow
   */
  // @ts-ignore TS6133 - reserved for future use
  const _handleSave = React.useCallback(async (): Promise<void> => {
    if (appState.view !== 'designer') {
      return;
    }

    const { saveWorkflowToApi } = useWorkflowStore.getState();

    const savedMessage = document.createElement('div');
    savedMessage.style.cssText = `
      position: fixed;
      top: 80px;
      right: 24px;
      padding: 12px 20px;
      background: #3b82f6;
      color: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
    `;
    savedMessage.textContent = 'Saving workflow...';
    document.body.appendChild(savedMessage);

    try {
      const result = await saveWorkflowToApi();

      if (result?.success) {
        savedMessage.style.background = '#10b981';
        savedMessage.textContent = '✓ Workflow saved successfully';
      } else {
        savedMessage.style.background = '#ef4444';
        const firstError = result?.errors?.[0]?.message;
        const errorMsg = isDevelopment() && firstError
          ? `Failed to save workflow:\n${firstError}`
          : (firstError || 'Failed to save workflow');
        savedMessage.textContent = '✗ ' + errorMsg;
        savedMessage.style.whiteSpace = 'pre-wrap';
        savedMessage.style.maxWidth = '600px';
        savedMessage.style.wordBreak = 'break-word';
      }

      setTimeout(() => { savedMessage.remove(); }, 3000);
    } catch (error) {
      console.error('Save error:', error);
      savedMessage.style.background = '#ef4444';
      const errorMsg = getErrorMessage(error, 'Unknown error');
      const displayMsg = isDevelopment()
        ? `Error saving workflow:\n${errorMsg}`
        : `Error saving workflow: ${error instanceof Error ? error.message : 'Unknown error'}`;
      savedMessage.textContent = '✗ ' + displayMsg;
      savedMessage.style.whiteSpace = 'pre-wrap';
      savedMessage.style.maxWidth = '600px';
      savedMessage.style.wordBreak = 'break-word';
      setTimeout(() => { savedMessage.remove(); }, 3000);
    }
  }, [appState]);

  /**
   * Render content based on app state
   */
  const renderContent = () => {
    if (appState.view === 'loading') {
      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '24px', animation: 'pulse 2s infinite' }}>
            {appState.project.IconUrl
              ? <BizFirstIcon icon={appState.project.IconUrl} size={48} />
              : <FolderIcon size={48} color={theme === 'dark' ? '#f8fafc' : '#111827'} />
            }
          </div>
          <div style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px', color: theme === 'dark' ? '#f8fafc' : '#111827' }}>
            Loading {appState.project.Name}...
          </div>
          <div style={{ fontSize: '14px', color: theme === 'dark' ? '#94a3b8' : '#64748b' }}>
            Fetching workflows and process data
          </div>
        </div>
      );
    }

    if (appState.view === 'open-target-error') {
      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>⚠️</div>
          <div style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: theme === 'dark' ? '#f8fafc' : '#111827' }}>
            Couldn't open the requested workflow
          </div>
          <div style={{ fontSize: '14px', color: theme === 'dark' ? '#94a3b8' : '#64748b', maxWidth: '480px', marginBottom: '20px' }}>
            {appState.message}
          </div>
          <button
            onClick={() => setAppState({ view: 'dashboard' })}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: '#309B48',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to Dashboard
          </button>
        </div>
      );
    }

    if (appState.view === 'dashboard') {
      const dashboardKey = appState.dashboardView
        ? `${appState.dashboardView.type}-${
            appState.dashboardView.type === 'processes'
              ? (appState.dashboardView as any).project.ProjectID
              : appState.dashboardView.type === 'threads'
              ? `${(appState.dashboardView as any).project.ProjectID}-${(appState.dashboardView as any).process.ProcessID}`
              : 'projects'
          }`
        : 'dashboard';

      return (
        <UnifiedDashboard
          key={dashboardKey}
          onProcessThreadSelect={handleProcessThreadSelect}
          initialView={appState.dashboardView}
        />
      );
    }

    if (appState.view === 'settings') {
      return (
        <Settings
          defaultTab={appState.defaultTab}
          onModuleSelect={(item, fromTab) => setAppState({ view: 'module', moduleItem: item, fromTab })}
        />
      );
    }

    if (appState.view === 'observer') {
      return <ObserverConfigPage />;
    }

    if (appState.view === 'hil-test') {
      return <HilV13TestPage />;
    }

    if (appState.view === 'build-with-ai') {
      return (
        <FlowStudioBuildWithAIPage
          agent={{ studioName: 'Flow Studio', githubUrl: FLOW_STUDIO_AI_AGENT_GITHUB_URL }}
          theme={theme}
        />
      );
    }

    // Module viewer — renders the selected integrated module page
    if (appState.view === 'module') {
      return (
        <ModuleViewer
          item={appState.moduleItem}
          onBack={() => setAppState({ view: 'settings', defaultTab: appState.fromTab })}
        />
      );
    }

    if (appState.view === 'designer') {
      const { project, processThread } = appState.processThreadData;
      const versionId = processThread.CurrentVersionID || processThread.ProcessThreadID;
      const projectName = project?.Name || '';
      const workflowName =
        processThread.Name ||
        (projectName ? `${projectName} Workflow 1` : undefined);

      return (
        <div style={{ width: '100%', height: '100%' }}>
          <WorkflowEditor
            processThreadId={processThread.ProcessThreadID}
            versionId={versionId}
            workflowName={workflowName}
          />
        </div>
      );
    }

    return null;
  };

  if (!templatesLoaded) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme === 'dark' ? '#020617' : '#ffffff',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '24px', animation: 'pulse 2s infinite' }}>
          <SettingsIcon size={48} />
        </div>
        <div style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px', color: theme === 'dark' ? '#f8fafc' : '#111827' }}>
          Loading Workflow Designer...
        </div>
        <div style={{ fontSize: '14px', color: theme === 'dark' ? '#94a3b8' : '#64748b' }}>
          Initializing node templates
        </div>
      </div>
    );
  }

  // Derive actor ID for pending HIL loader.
  // Format matches BaseHilNodeExecutor.GetCurrentUserIdentifier() → "user:{userId}".
  // Override by storing a custom key: localStorage.setItem('hilActorID', 'your-actor-id')
  const hilActorID =
    localStorage.getItem('hilActorID') ??
    (localStorage.getItem('userId') ? `user:${localStorage.getItem('userId')}` : undefined) ??
    undefined;

  return (
    <HilProvider submitClient={inboxItemHilSubmitClient}>
      {/* Wraps the whole app (not just a floating overlay) so the "HIL Actions" dock
          tab — rendered deep inside AppLayout/WorkflowEditor/FlowObserverPanel — also
          has access to the Atlas form-content renderer. Forms/chat used to render via
          a blocking HilOverlay/HilChatWindow mounted here; they now render inline
          inside that dock tab (see HilObservePanel.tsx) so a suspended workflow no
          longer blocks the whole canvas and the session survives the tab being closed. */}
      <HilFormRendererProvider
        value={(props) => (
          <AtlasFormPresentationContent
            {...props}
            apiBaseUrl={apiConfig.baseUrl}
            getAuthToken={() => localStorage.getItem('authToken') ?? undefined}
            darkTheme={theme === 'dark'}
          />
        )}
      >
        <HilPendingLoader actorID={hilActorID} />
        <FlowObserverInit />
        <AppLayout
          headerProps={{ breadcrumbs: getHeaderBreadcrumbs() }}
          activeView={getCurrentMenuView()}
          onViewChange={handleMenuViewChange}
          onBuildWithAI={() => setAppState({ view: 'build-with-ai' })}
        >
          {renderContent()}
        </AppLayout>
        <ToastContainer />
      </HilFormRendererProvider>
    </HilProvider>
  );
};

// Add pulse animation in global styles
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
document.head.appendChild(style);
